import axios from '@api';

import {
  getWithCredentials,
  transformDetails,
  transformGlobalFilters,
  transformGlobalFiltersV2,
  transformRawData,
  transformTurfSimulatorData,
} from './utilities/helpers';
import { getObjectWithoutEmptyValues } from '@utilities';

const TURF_SIMULATOR_DATA_PAGE_SIZE = 500;

/**
 * export raw data
 * @param {Object} options
 * @param {boolean} [options.includeDisqualified] include DISQUALIFIED responses (Calypso only)
 * @param {string} [options.language] 2-digit language code
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const exportRawData = async ({ includeDisqualified, language, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      include_disqualified: includeDisqualified,
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/rawdata/xlsx/',
    withCredentials: true,
  });
};

/**
 * export raw data v2
 * @param {Object} options
 * @param {string} [options.language] 2-digit language code
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const exportRawDataV2 = async ({ language, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/raw-data-v2/xlsx/',
    withCredentials: true,
  });
};

/**
 * export raw data as SPSS
 * @param {Object} options
 * @param {string} [options.language] 2-digit language code
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const exportRawDataSpss = async ({ language, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/rawdata/spss/',
    withCredentials: true,
  });
};

/**
 * export summary tab
 * @param {Object} options
 * @param {number|string} options.surveyId
 * @param {string} [options.language]
 * @return {Promise}
 */
export const exportSummaryData = async ({ language, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/summary/xlsx/',
    withCredentials: true,
  });
};

/**
 * export crosstabs and stattesting xlsx file
 * @param {Object} options
 * @param {string} [options.language] 2-digit language code
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const exportStatXtabData = async ({ language, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/xtab_stat_sig/xlsx/',
    withCredentials: true,
  });
};

/**
 * export a custom crosstab xlsx built from a user-defined banner.
 * Triggers an async backend task; the file is delivered via the notification center.
 * @param {Object} options
 * @param {Object} options.payload custom crosstab definition (banner, questions, filters, settings)
 * @param {Number|String} options.surveyId
 * @param {string} [options.language] 2-digit language code
 * @return {Promise}
 */
export const exportCustomCrosstabs = async ({ language, payload, surveyId }) => {
  return axios({
    data: payload,
    method: 'post',
    params: {
      language,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/custom-crosstabs/xlsx/',
    withCredentials: true,
  });
};

/**
 * export TURF analysis
 * @param {Object} options
 * @param {ExportQuery} [options.query]
 * @return {Promise}
 */
export const exportTurfData = async ({ query }) => {
  return axios({
    method: 'get',
    params: query,
    url: '/v2/api/survey-dashboard/export/turf/xlsx/',
    withCredentials: true,
  });
};

/**
 * export maxdiff preference share file
 * @param {Object} options
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const exportMaxDiffPrefShareData = async ({ query = {}, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      ...getObjectWithoutEmptyValues(query),
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/preference-share/xlsx/',
    withCredentials: true,
  });
};

/**
 * read details (response counts)
 * @param {Object} options
 * @param {string} [options.publicToken]
 * @param {number} [options.signal] AbortController signal
 * @param {number} options.surveyId Insights survey id
 * @return {Promise}
 */
export const readDetails = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/details/${surveyId}`,
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformDetails(data));
};

/**
 * read global filters
 * @param {Object} options
 * @param {string} options.publicToken
 * @param {number} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readGlobalFilters = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/global-filters/${surveyId}`,
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformGlobalFilters(data));
};

/**
 * read global filters v2
 * @param {Object} options
 * @param {string} options.publicToken
 * @param {number} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readGlobalFiltersV2 = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/global-filters-v2/',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformGlobalFiltersV2(data));
};

/**
 * read the global-filters catalog (new dedicated endpoint; INSRV-8581). Returns the raw
 * `{ sections }` payload; the Survey Responses adapter `buildGlobalFilterFacetsFromCatalog`
 * maps it to the GlobalFilters `Facet[]`.
 * @param {Object} options
 * @param {string} [options.publicToken]
 * @param {AbortSignal} [options.signal]
 * @param {number|string} options.surveyId Insights instant survey id
 * @return {Promise}
 */
export const readFilterCatalog = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/filter-catalog/${surveyId}`,
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => data);
};

/**
 * read raw data
 * @param {Object} options
 * @param {boolean} [options.includeDisqualified] include DISQUALIFIED responses (Calypso only)
 * @param {string} [options.language] 2-digit language code
 * @param {string} options.publicToken
 * @param {AbortSignal} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readRawData = async ({
  includeDisqualified,
  language,
  publicToken,
  signal,
  surveyId,
}) => {
  return axios({
    method: 'get',
    params: {
      include_disqualified: includeDisqualified,
      language,
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/raw-data/table',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformRawData(data));
};

/**
 * read raw data v2
 * @param {Object} options
 * @param {string} [options.language] 2-digit language code
 * @param {string} options.publicToken
 * @param {number} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readRawDataV2 = async ({ language, publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      language,
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/raw-data-v2/table',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformRawData(data));
};

const readTurfSimulatorDataPages = async ({
  endpoint,
  includeDisqualified = false,
  language,
  pageSize = TURF_SIMULATOR_DATA_PAGE_SIZE,
  signal,
  surveyId,
}) => {
  const items = [];
  let page = 1;
  let pages = 1;

  do {
    const response = await axios({
      method: 'get',
      params: {
        include_disqualified: includeDisqualified,
        language,
        page,
        size: pageSize,
        survey_id: surveyId,
      },
      signal,
      url: endpoint,
      withCredentials: true,
    });
    const payload = response?.data ?? {};

    items.push(...(Array.isArray(payload.items) ? payload.items : []));
    pages = Number.isFinite(Number(payload.pages)) ? Number(payload.pages) : page;
    page += 1;
  } while (page <= pages);

  return transformTurfSimulatorData(items);
};

/**
 * read TURF simulator data
 * @param {Object} options
 * @param {boolean} [options.includeDisqualified]
 * @param {string} [options.language] 2-digit language code
 * @param {number} [options.pageSize]
 * @param {number} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readTurfSimulatorData = async (options) =>
  readTurfSimulatorDataPages({
    ...options,
    endpoint: '/v2/api/survey-dashboard/turf-simulator-data',
  });

/**
 * read TURF simulator data v2
 * @param {Object} options
 * @param {boolean} [options.includeDisqualified]
 * @param {string} [options.language] 2-digit language code
 * @param {number} [options.pageSize]
 * @param {number} [options.signal] AbortController signal
 * @param {number|string} options.surveyId
 * @return {Promise}
 */
export const readTurfSimulatorDataV2 = async (options) =>
  readTurfSimulatorDataPages({
    ...options,
    endpoint: '/v2/api/survey-dashboard/turf-simulator-data-v2',
  });

export * from './response_quality';
export * from './survey_responses';
